import type { EntityAdapter, EntityState } from '@ngrx/entity';
import { createEntityAdapter } from '@ngrx/entity';
import type { NormalizedSectionEntity } from '@web/types';
import { on } from '@ngrx/store';
import { createImmerReducer } from 'ngrx-immer/store';

import { arrayLengthEq } from '@common/helpers';

import ColumnsActions from '../columns/columns.actions';
import SectionsActions from './sections.actions';

export const SECTIONS_FEATURE_KEY = 'sections';

export type SectionsState = EntityState<NormalizedSectionEntity>;

export const sectionsAdapter: EntityAdapter<NormalizedSectionEntity> = createEntityAdapter<NormalizedSectionEntity>({
    selectId: (s: NormalizedSectionEntity) => s.id,
    sortComparer: false,
});

export const sectionsReducer = createImmerReducer<SectionsState>(
    sectionsAdapter.getInitialState(),
    on(SectionsActions.init, (state: SectionsState, { sections }) => sectionsAdapter.setAll(sections, state)),
    on(SectionsActions.sectionCreated, SectionsActions.addSection, (state: SectionsState, { section }) =>
        sectionsAdapter.addOne(section, state)
    ),
    on(SectionsActions.copyManySections, SectionsActions.addManySections, (state: SectionsState, { sections }) =>
        sectionsAdapter.addMany(sections, state)
    ),
    on(SectionsActions.removeSection, (state: SectionsState, { section }) =>
        sectionsAdapter.removeOne(section.id, state)
    ),
    on(SectionsActions.removeManySections, (state, { sectionsIds }) => sectionsAdapter.removeMany(sectionsIds, state)),
    on(SectionsActions.updateSectionProperties, (state, { update }) => sectionsAdapter.updateOne(update, state)),
    // on(SectionsActions.moveUp, (state: SectionsState, { index }) => {
    //     const blockId = state.ids[index];
    //     state.ids[index] = state.ids[index - 1];
    //     state.ids[index - 1] = blockId;
    //     return state;
    // }),
    // on(SectionsActions.moveDown, (state: SectionsState, { index }) => {
    //     const blockId = state.ids[index];
    //     state.ids[index] = state.ids[index + 1];
    //     state.ids[index + 1] = blockId;
    //     return state;
    // }),
    // react on columns state change
    on(ColumnsActions.columnCreated, ColumnsActions.columnAdded, (state: SectionsState, { column, index }) => {
        const section = state.entities[column.parentId];
        section.children.splice(index, 0, column.id);

        return state;
    }),
    on(ColumnsActions.removeColumn, (state: SectionsState, { column }) => {
        const columnId = column.id;
        const sectionId = column.parentId;
        const section = state.entities[sectionId];
        section.children = section.children.filter((id) => id !== columnId);

        // A single column no longer needs the gap the row put between them.
        if (arrayLengthEq(section.children, 1) && section.entitySubType === 'default') {
            section.props.columnGap.value = 0;
        }

        return state;
    }),
    on(
        ColumnsActions.columnMoved,
        (state: SectionsState, { prevSectionId, currentSectionId, previousIndex, currentIndex }) => {
            const oldSection = state.entities[prevSectionId];
            const currentSection = state.entities[currentSectionId];

            const column = oldSection.children.splice(previousIndex, 1)[0];
            currentSection.children.splice(currentIndex, 0, column);
            return state;
        }
    ),
    on(ColumnsActions.columnCopied, (state: SectionsState, { column, index }) => {
        const section = state.entities[column.parentId];
        section.children.splice(index, 0, column.id);
        return state;
    })
);
